import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { queryClient } from "@/lib/queryClient";
import { supabase } from "@/lib/supabase";
import type { UserProfile, UserRole } from "@/lib/types";

interface AuthContextValue {
  session: Session | null;
  profile: UserProfile | null;
  role: UserRole | null;
  loading: boolean;
  signOut: () => Promise<void>;
  /** Re-fetch the users row after a profile edit so header/nav update at once. */
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Moves a recovery-link landing to /reset-password BEFORE the router reads
 * the URL; main.tsx calls it ahead of the first render. The field app's
 * reset email lands on the site root, and the PASSWORD_RECOVERY listener
 * below can miss the event (supabase-js may fire it before AuthProvider
 * subscribes), which dropped the recovery session on the dashboard.
 *
 * This used to reroute from an effect after mount, which lost the link both
 * ways: navigating stripped the hash tokens before supabase-js had read them
 * (its URL detection waits on a lock), so a valid link showed "Reset link
 * expired"; and while the lazy reset page loaded, the root route could still
 * redirect the session to the dashboard. Query and hash ride along here:
 * supabase-js reads its tokens from the hash, and a token_hash link needs
 * its query until the user clicks Continue.
 */
export function routeRecoveryLanding() {
  const { pathname, search, hash } = window.location;
  const recovery = hash.includes("type=recovery") || search.includes("type=recovery");
  if (recovery && pathname !== "/reset-password") {
    window.history.replaceState(window.history.state, "", `/reset-password${search}${hash}`);
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    // Initial session load
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      if (!data.session) {
        setProfile(null);
        setLoading(false);
      }
    });

    // Subscribe to subsequent changes
    const { data: sub } = supabase.auth.onAuthStateChange((event, sess) => {
      setSession(sess);
      if (!sess) {
        setProfile(null);
        setLoading(false);
      }
      // Drop every cached query on sign-out (explicit OR session-expiry):
      // on a shared browser the next user was served the previous user's
      // cached leases/entities/profile until staleTime elapsed.
      if (event === "SIGNED_OUT") {
        queryClient.clear();
      }
      // A recovery-link session is NOT a normal sign-in: supabase-js fires
      // this after consuming the email link's tokens, wherever the redirect
      // landed. Route to the set-new-password screen instead of letting the
      // user fall through to the dashboard still holding their old password.
      if (event === "PASSWORD_RECOVERY") {
        navigate("/reset-password", { replace: true });
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
    // navigate is stable for the router's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Whenever the session changes to a real user, fetch the profile row.
  useEffect(() => {
    let cancelled = false;
    if (!session?.user?.id) return;

    setLoading(true);
    supabase
      .from("users")
      .select("*")
      .eq("id", session.user.id)
      .single()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          // Profile row should always exist (trigger creates it on signup);
          // log + leave profile null so RequireRole can route away cleanly.
          console.error("[AuthContext] profile fetch failed:", error.message);
          setProfile(null);
        } else if (data && !data.is_active) {
          // Deactivated: the DB already gives this account no role and no
          // data, and the Auth ban blocks a fresh sign-in (which shows the
          // error on the login page). Drop the lingering session here.
          setProfile(null);
          void supabase.auth.signOut({ scope: "local" });
        } else {
          setProfile(data);
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      profile,
      role: profile?.role ?? null,
      loading,
      signOut: async () => {
        // scope "local": revoke THIS browser's refresh token only. auth-js
        // defaults to "global", which revoked every signed-in device on the
        // account — including field tablets. A desk sign-out at 5pm stranded
        // the boat the next morning: the tablet's next online refresh got
        // 400 "Invalid Refresh Token" and dropped the crew on a login screen
        // offshore, with no signal to sign back in. The mobile app already
        // passes this scope (MOBILE_APP/src/lib/auth.tsx); the web client was
        // missed because it lives in a different repo.
        await supabase.auth.signOut({ scope: "local" });
      },
      refreshProfile: async () => {
        const userId = session?.user?.id;
        if (!userId) return;
        const { data, error } = await supabase
          .from("users")
          .select("*")
          .eq("id", userId)
          .single();
        if (!error) setProfile(data);
      },
    }),
    [session, profile, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
